import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, PackageCheck, PhoneCall, ClipboardCheck, CalendarCheck, DoorOpen, ArrowRight, FileText } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLines } from "@/components/Reveal";

const qualifies = [
  "Adults (18+) living with a substance use disorder who need 24/7 residential structure to stay safe and engaged in recovery",
  "People who meet ASAM Level 3.3 criteria — clinically managed, high-intensity residential care paced for those who benefit from a slower, more repetitive, supported approach",
  "Individuals with cognitive or functional impairments (temporary or ongoing) that make faster-paced programs a poor fit",
  "Those who are medically stable — no active withdrawal requiring hospital-level monitoring, no acute psychiatric crisis",
  "Men, for the Male Manor in Petersburg; women, for the Female Home (interest list open now)",
  "Anyone willing to participate in a structured daily schedule, house guidelines, and their individualized recovery plan",
];

const notFit = [
  "Active medical detox needs (we will help coordinate a detox placement first)",
  "Acute psychiatric emergency or active danger to self or others — call 911 or 988 now",
  "Need for 24-hour nursing or hospital-level medical care",
];

const bring = [
  "Government-issued photo ID (or whatever ID you have — we can help replace it)",
  "Insurance / Medicaid card and any benefits paperwork",
  "Current prescription medications in their original, labeled bottles",
  "About 7 days of comfortable, weather-appropriate clothing and sleepwear",
  "Toiletries (alcohol-free), towels optional — we provide bedding",
  "Important phone numbers, legal or court paperwork, and any treatment records you have",
  "A notebook or journal, reading material, and photos of the people you love",
];

const leave = [
  "Alcohol, drugs, paraphernalia, or products containing alcohol (mouthwash, some colognes)",
  "Weapons of any kind",
  "Large amounts of cash, valuables, or unapproved electronics",
  "Outside food or energy drinks (meals and snacks are provided)",
];

const steps = [
  { icon: FileText, title: "Submit a referral", text: "You, a family member, a counselor, or a case manager fills out the short referral form. It takes about three minutes." },
  { icon: PhoneCall, title: "Screening call within one business day", text: "A staff member calls to listen, answer questions, and confirm the basics — age, home preference, medical stability, and goals." },
  { icon: ClipboardCheck, title: "Clinical assessment", text: "We complete an ASAM-based assessment (often by phone or telehealth) to confirm Level 3.3 is the right fit and to begin your individualized plan." },
  { icon: CalendarCheck, title: "Admission decision & move-in date", text: "We confirm bed availability, coordinate with insurance or referral sources, and set a move-in date. If we are not the right fit, we help you find who is." },
  { icon: DoorOpen, title: "Arrival & orientation", text: "You are welcomed at the door, shown your room, introduced to the house, and walked through the daily rhythm. Day one is about settling in." },
];

export default function Admissions() {
  return (
    <div data-testid="admissions-page">
      <section className="relative overflow-hidden bg-[#FAF8F5] grain">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#F3E8FF] blur-3xl opacity-80" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 text-center relative">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-4">Admissions</p>
          <MaskedLines
            className="font-display font-black text-[#2E1065] text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight"
            lines={["Who we serve.", "How to get here."]}
          />
          <Reveal delay={0.4}>
            <p className="mt-7 text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl mx-auto" data-testid="admissions-intro">
              No confusing paperwork, no runaround. Here is exactly who qualifies for our ASAM 3.3 homes, what to
              bring on move-in day, and what happens between your first message and your first night.
            </p>
            <p className="mt-4 font-display italic text-[#5B21B6] text-lg">Compassion. Opportunity. Motivation.</p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-5 gap-12" data-testid="who-qualifies-section">
        <Reveal className="lg:col-span-3">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-3">Who Qualifies</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1E1B4B]" data-testid="qualifies-heading">
            A good fit for ASAM Level 3.3
          </h2>
          <ul className="mt-8 space-y-4">
            {qualifies.map((q, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`qualifies-item-${i}`}>
                <CheckCircle2 size={20} className="text-emerald-600 mt-0.5 shrink-0" />
                <span className="text-base text-slate-700 leading-relaxed">{q}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-2">
          <div className="bg-white rounded-3xl border border-purple-100 shadow-sm p-7 lg:sticky lg:top-28" data-testid="not-fit-card">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-slate-500 mb-3">Not the right fit yet</p>
            <h3 className="font-display font-bold text-xl text-[#2E1065]">We'll still help you find care</h3>
            <ul className="mt-5 space-y-3">
              {notFit.map((n, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed">
                  <XCircle size={18} className="text-[#B45309] mt-0.5 shrink-0" /> {n}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-xs text-slate-500 leading-relaxed">
              Not sure? Submit the referral anyway. Our screening call exists to sort this out with you — never against you.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-[#F4EFE6] py-20 lg:py-28" data-testid="what-to-bring-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-3">Move-In Day</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1E1B4B]" data-testid="bring-heading">
              What to bring — and what to leave at home
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <Reveal>
              <div className="bg-white rounded-3xl border border-purple-100 shadow-sm p-8 h-full" data-testid="bring-card">
                <div className="w-12 h-12 rounded-2xl bg-[#F3E8FF] flex items-center justify-center mb-5"><PackageCheck size={22} className="text-[#5B21B6]" /></div>
                <h3 className="font-display font-bold text-2xl text-[#2E1065]">Please bring</h3>
                <ul className="mt-5 space-y-3">
                  {bring.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed" data-testid={`bring-item-${i}`}>
                      <CheckCircle2 size={17} className="text-emerald-600 mt-0.5 shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="bg-[#2E1065] text-[#FAF8F5] rounded-3xl p-8 h-full grain relative" data-testid="leave-card">
                <div className="w-12 h-12 rounded-2xl bg-amber-400 flex items-center justify-center mb-5"><XCircle size={22} className="text-[#2E1065]" /></div>
                <h3 className="font-display font-bold text-2xl">Please leave at home</h3>
                <ul className="mt-5 space-y-3">
                  {leave.map((l, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-purple-100 leading-relaxed" data-testid={`leave-item-${i}`}>
                      <XCircle size={17} className="text-amber-400 mt-0.5 shrink-0" /> {l}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-xs text-purple-300 leading-relaxed">
                  Belongings are gently checked on arrival for everyone's safety. Anything that can't stay is held or sent home — never thrown away without asking.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20 lg:py-28" data-testid="intake-process-section">
        <Reveal className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-3">The Intake Process</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1E1B4B]" data-testid="intake-heading">
            Five steps from first message to first night
          </h2>
        </Reveal>
        <ol className="mt-14 space-y-6">
          {steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <li className="flex gap-5 sm:gap-7 items-start bg-white rounded-3xl border border-purple-100 shadow-sm p-6 sm:p-7 hover:shadow-xl transition-shadow duration-500" data-testid={`intake-step-${i}`}>
                <div className="shrink-0 flex flex-col items-center gap-2">
                  <span className="font-display font-black text-3xl text-[#F59E0B] leading-none">0{i + 1}</span>
                  <div className="w-11 h-11 rounded-2xl bg-[#5B21B6] text-white flex items-center justify-center"><s.icon size={20} /></div>
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl text-[#2E1065]">{s.title}</h3>
                  <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">{s.text}</p>
                </div>
              </li>
            </Reveal>
          ))}
        </ol>
      </section>

      <section className="bg-[#2E1065] py-20 text-center relative grain" data-testid="admissions-cta">
        <Reveal>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-[#FAF8F5]" data-testid="admissions-cta-heading">
            Step one is a three-minute form.
          </h2>
          <p className="mt-4 text-purple-200 max-w-xl mx-auto text-base">
            You'll get an instant confirmation email, and a real person will call within one business day.
          </p>
          <Link
            to="/contact"
            data-testid="admissions-cta-button"
            className="mt-8 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-9 py-4 rounded-full shadow-xl shadow-amber-500/20 transition-colors duration-300"
          >
            Start a Referral <ArrowRight size={18} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
